// Reworked and typed from https://github.com/hdodov/active-timeout.js

import { useIsomorphicRequestAnimationLayout } from "./dom";
import { listener, ListenerTypes } from "./listener";
import { store } from "./store";
import { uuid } from "./utils";

/**
 * NOTE: Needs to be instantiated after PageVisibility Tracker as the
 * event emmiter (listener) for ListenerTypes.visibilityChange won't be
 * setup yet
 *
 */
export const activeHeartbeat = (() => {
  const monitors = new Map();

  const start = () => {
    listener.on(ListenerTypes.visibilityChange, (isPageHidden: boolean) => {
      if (!isPageHidden) {
        // The page is not hidden, but a change occured. That means
        // is *was* previously hidden, so the timers receive an event
        // that tells them to ignore the next tick since it would
        // contain the inactive time.

        for (let monitor of monitors.values()) {
          monitor();
        }
      }
    });
  };

  /**
   * Measure ~60 intervals per second and ignore ones where the user was
   * inactive. Repeat until the predicate returns a falsy value.
   * @param  {function} predicate Stops pulsing when it returns a falsy value.
   */
  const pulse = (predicate: (args: any) => boolean) => {
    let ignoreTicks = 0;
    let listener = function () {
      ignoreTicks += 1;
    };

    const pulseUUID = uuid();
    monitors.set(pulseUUID, listener);

    (function measure(last?: number | undefined) {
      var proceed = false,
        now = Date.now();

      // Whenever the page loses focus, the next tick should be
      // ignored, otherwise the inactive time would be added and this
      // whole charade would be meaningless.
      if (ignoreTicks > 0) {
        ignoreTicks -= 1;
        proceed = true;
      } else {
        if (store.get("isPageHidden")) {
          // Page is hidden, continue to pulse and wait for focus.
          proceed = true;
        } else {
          if (!last) {
            // First iteration of the recursion, continue and wait
            // for some time to pass.
            proceed = true;
          } else {
            // Finally, the predicate decides whether to continue.
            const timerHasBeenCleared = !monitors.has(pulseUUID);

            proceed = timerHasBeenCleared ? false : predicate(now - last);
          }
        }
      }

      if (proceed === true) {
        useIsomorphicRequestAnimationLayout(function () {
          measure(now);
        });
      } else {
        clearTimer(pulseUUID);
      }
    })();

    return pulseUUID;
  };

  /**
   * Count *active* passed time until a predicate returns a falsy value.
   * @param  {function} predicate Stops the counter when it returns a falsy value.
   */
  const count = (predicate: any) => {
    var time = 0;

    const pulseUUID = pulse(function (tick) {
      time += tick;
      return predicate(time, tick);
    });

    return pulseUUID;
  };

  const clearTimer = (pulseUUID: string) => {
    monitors.delete(pulseUUID);
  };

  /**
   * Invoke a function after the user has spent a set amount of *active* time
   * on the page. Optionally provide a callback for each tick.
   * @param  {function} completeCallback Function to call when the timeout
   *                                     completes.
   * @param  {function} tickCallback     Optional function to call upon each
   *                                     timer tick.
   * @param  {number}   time             How much milliseconds to wait.
   */
  const timeout = (
    completeCallback: any,
    tickCallback?: any,
    time?: any,
    name?: any
  ) => {
    if (typeof tickCallback === "number") {
      time = tickCallback;
      tickCallback = null;
    }

    const pulseUUID = count(function (passed: any, tick: any) {
      if (typeof tickCallback === "function") {
        tickCallback(time - passed, tick);
      }

      if (passed >= time) {
        completeCallback();
        return false;
      } else {
        return true;
      }
    });

    return pulseUUID;
  };

  return {
    start,
    set: timeout,
    count: count,
    pulse: pulse,
    clearTimer,
  };
})();
